// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import * as React from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom"; // TODO: useRouter → useNavigate (nav(x) → nav(x), router.replace(x) → nav(x, {replace:true}))
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import {
  Archive,
  Eye,
  FileText,
  Pencil,
  Trash2,
  Upload,
  XCircle,
} from "lucide-react";
import {
  Badge,
  Button,
  ConfirmDialog,
  FilterCombobox,
  FilterSearch,
  FilterSelect,
  FiltersToolbar,
  PostingStateBadge,
  useToast,
} from "@chotca/ui";
import { type PostingState } from "@chotca/config";
import { useAdminOrgs, useAdminPostings } from "@chotca/api-client";
import { useTableFilters } from "@/lib/use-table-filters";
import { getSdk } from "@chotca/api-client";
import {
  DataTable,
  usePagination,
  type BulkAction,
  type Column,
  type RowAction,
} from "@/components/data-table";

type PostingRow = {
  id: string;
  title: string;
  state: string;
  org: { id: string; name: string; district: string };
  applicantCount: number;
  newApplicantCount: number;
};

export function PostingsClient() {
  const t = useTranslations("admin");
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const filters = useTableFilters({
    search: { debounce: 300 },
    orgId: {},
    state: {},
  });
  const { page, pageSize, setPage, setPageSize } = usePagination();
  // Default the state filter to OPEN ("đang tuyển"): an absent ?state= param
  // means show recruiting posts. The explicit "ALL" sentinel (set when the
  // user picks "Tất cả") drops the filter so every state shows.
  const stateSelected =
    (filters.values.state ?? "OPEN") === "ALL"
      ? ""
      : (filters.values.state ?? "OPEN");
  const stateApplied = filters.applied.state ?? "OPEN";
  const stateQuery = stateApplied === "ALL" ? undefined : stateApplied;
  const { data, isLoading, isFetching } = useAdminPostings({
    search: filters.applied.search,
    orgId: filters.applied.orgId,
    state: stateQuery,
    page,
    limit: pageSize,
  });
  // Org filter is a searchable select: the in-panel search drives a debounced
  // server-side org lookup, while a name cache keeps the selected org's label
  // visible even after the search text changes (or on a deep-linked ?orgId=).
  const [orgSearchInput, setOrgSearchInput] = React.useState("");
  const [orgSearch, setOrgSearch] = React.useState("");
  React.useEffect(() => {
    const t = setTimeout(() => setOrgSearch(orgSearchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [orgSearchInput]);
  const { data: orgsData, isLoading: orgsLoading } = useAdminOrgs({
    search: orgSearch || undefined,
  });
  const orgItems = (orgsData?.items ?? []) as { id: string; name: string }[];
  const orgNameCache = React.useRef<Map<string, string>>(new Map());
  for (const o of orgItems) orgNameCache.current.set(o.id, o.name);
  const selectedOrgId = filters.values.orgId ?? "";
  const orgOptions = orgItems.map((o) => ({ value: o.id, label: o.name }));
  if (selectedOrgId && !orgOptions.some((o) => o.value === selectedOrgId)) {
    orgOptions.unshift({
      value: selectedOrgId,
      label: orgNameCache.current.get(selectedOrgId) ?? "Doanh nghiệp đã chọn",
    });
  }

  const [confirmDelete, setConfirmDelete] = React.useState<PostingRow | null>(
    null,
  );
  const [confirmCancel, setConfirmCancel] = React.useState<PostingRow | null>(
    null,
  );
  const [pendingId, setPendingId] = React.useState<string | null>(null);

  function refresh() {
    qc.invalidateQueries({ queryKey: ["admin", "postings"] });
  }

  const close = useMutation({
    mutationFn: (id: string) => getSdk().admin.closePosting(id),
    onMutate: (id) => setPendingId(id),
    onSettled: () => setPendingId(null),
    onSuccess: () => {
      refresh();
      toast({ tone: "success", title: "Đã đóng tin" });
    },
  });
  const cancel = useMutation({
    mutationFn: (id: string) => getSdk().admin.cancelPosting(id),
    onSuccess: () => {
      refresh();
      toast({ tone: "success", title: "Đã huỷ tin" });
      setConfirmCancel(null);
    },
    onError: () => toast({ tone: "error", title: "Không thể huỷ" }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => getSdk().admin.deletePosting(id),
    onSuccess: () => {
      refresh();
      toast({ tone: "success", title: "Đã xoá" });
      setConfirmDelete(null);
    },
    onError: () => toast({ tone: "error", title: "Không thể xoá" }),
  });

  const list = (data?.items ?? []) as PostingRow[];

  const columns: Column<PostingRow>[] = [
    {
      key: "title",
      header: "Tiêu đề",
      cell: (p) => (
        <Link
          to={`/postings/${p.id}`}
          className="font-semibold text-ink hover:underline"
        >
          {p.title}
        </Link>
      ),
    },
    {
      key: "org",
      header: "Doanh nghiệp",
      cell: (p) => (
        <Link
          to={`/orgs/${p.org.id}`}
          className="text-xs hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          {p.org.name} <span className="text-ash">· {p.org.district}</span>
        </Link>
      ),
    },
    {
      key: "applicants",
      header: "Ứng viên",
      align: "right",
      cell: (p) => (
        <div className="flex items-center justify-end gap-2">
          <span className="font-semibold">{p.applicantCount}</span>
          {p.newApplicantCount > 0 ? (
            <Badge tone="sun">+{p.newApplicantCount}</Badge>
          ) : null}
        </div>
      ),
    },
    {
      key: "state",
      header: "Trạng thái",
      cell: (p) => <PostingStateBadge state={p.state as PostingState} />,
    },
  ];

  const rowActions: RowAction<PostingRow>[] = [
    {
      key: "view",
      label: "Xem chi tiết & ứng viên",
      icon: Eye,
      onClick: (p) => nav(`/postings/${p.id}`),
    },
    {
      key: "edit",
      label: "Chỉnh sửa tin",
      icon: Pencil,
      onClick: (p) => nav(`/postings/${p.id}/edit`),
    },
    {
      key: "close",
      label: "Đóng tin (ngừng nhận ứng viên)",
      icon: Archive,
      onClick: (p) => close.mutate(p.id),
      show: (p) => p.state === "OPEN",
      loading: (p) => close.isPending && pendingId === p.id,
    },
    {
      key: "cancel",
      label: "Huỷ tin",
      icon: XCircle,
      onClick: (p) => setConfirmCancel(p),
      show: (p) => p.state === "OPEN",
      destructive: true,
    },
    {
      key: "delete",
      label: "Xoá tin",
      icon: Trash2,
      onClick: (p) => setConfirmDelete(p),
      show: (p) => p.applicantCount === 0,
      destructive: true,
    },
  ];

  const [bulkPending, setBulkPending] = React.useState(false);
  async function bulkClose(rows: PostingRow[]) {
    setBulkPending(true);
    try {
      await Promise.all(
        rows
          .filter((r) => r.state === "OPEN")
          .map((r) => getSdk().admin.closePosting(r.id)),
      );
      toast({ tone: "success", title: "Đã đóng các tin được chọn" });
      refresh();
    } catch {
      toast({ tone: "error", title: "Có lỗi xảy ra" });
    } finally {
      setBulkPending(false);
    }
  }
  const bulkActions: BulkAction<PostingRow>[] = [
    {
      key: "close",
      label: "Đóng tất cả",
      icon: Archive,
      onClick: bulkClose,
      loading: () => bulkPending,
    },
  ];

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-ink">{t("postingsTitle")}</h1>
        <div className="flex items-center gap-2">
          <Link
            to="/postings/import"
            className="inline-flex items-center gap-2 rounded-full border border-smoke bg-paper px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-mist"
          >
            <Upload size={16} strokeWidth={1.75} />
            Nhập từ Facebook
          </Link>
          <Link
            to="/posting-templates"
            className="inline-flex items-center gap-2 rounded-full border border-smoke bg-paper px-4 py-2 text-sm font-semibold text-ink transition-colors hover:bg-mist"
          >
            <FileText size={16} strokeWidth={1.75} />
            {t("postingTemplatesLink")}
          </Link>
        </div>
      </header>

      <FiltersToolbar
        hasActiveFilters={filters.hasAny}
        onClearAll={filters.clear}
        actions={
          <Button onClick={() => nav("/postings/new")}>
            + {t("createPosting")}
          </Button>
        }
      >
        <FilterSearch
          value={filters.values.search ?? ""}
          onChange={(v) => filters.set("search", v)}
          placeholder="Tìm theo tiêu đề..."
        />
        <FilterCombobox
          value={selectedOrgId}
          onValueChange={(v) => filters.set("orgId", v)}
          options={orgOptions}
          placeholder="Mọi doanh nghiệp"
          searchPlaceholder="Tìm doanh nghiệp..."
          search={orgSearchInput}
          onSearchChange={setOrgSearchInput}
          loading={orgsLoading}
        />
        <FilterSelect
          value={stateSelected}
          onValueChange={(v) => {
            // "" (Tất cả) → ALL sentinel; OPEN is the default → clear the param.
            if (v === "") filters.set("state", "ALL");
            else if (v === "OPEN") filters.set("state", "");
            else filters.set("state", v);
          }}
          placeholder={t("postingsFilterAll")}
          options={[
            { value: "OPEN", label: t("postingsFilterOpen") },
            { value: "CLOSED", label: t("postingsFilterClosed") },
            { value: "CANCELLED", label: t("postingsFilterCancelled") },
            { value: "EXPIRED", label: t("postingsFilterExpired") },
          ]}
        />
      </FiltersToolbar>

      <DataTable
        columns={columns}
        data={list}
        isLoading={isLoading}
        isFetching={isFetching}
        rowKey={(p) => p.id}
        rowActions={rowActions}
        bulkActions={bulkActions}
        page={page}
        pageSize={pageSize}
        total={data?.total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      <ConfirmDialog
        open={!!confirmCancel}
        onClose={() => setConfirmCancel(null)}
        title="Huỷ tin?"
        description="Toàn bộ ứng viên đang APPLIED sẽ chuyển sang REJECTED."
        confirmLabel="Huỷ tin"
        destructive
        loading={cancel.isPending}
        onConfirm={() => {
          if (confirmCancel) cancel.mutate(confirmCancel.id);
        }}
      />
      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title="Xoá tin?"
        description="Chỉ xoá được nếu chưa có ứng viên."
        confirmLabel="Xoá"
        destructive
        loading={remove.isPending}
        onConfirm={() => {
          if (confirmDelete) remove.mutate(confirmDelete.id);
        }}
      />
    </div>
  );
}
